import { lazy, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getNews } from 'services/news';
import { PlayerNews } from 'services/players';

import { useBrowsing } from 'hooks/use-browsing';
import { useQueryParam } from 'hooks/use-query-param';
import useScrollTo from 'hooks/use-scroll-to';
import { useViewport } from 'hooks/use-viewport';

import { Divider } from 'core/generic/elements/divider';

import styles from './news-list.module.scss';

const NewsCard = lazy(() => import('core/generic/components/cards/card-news'));
const Loading = lazy(() => import('core/generic/elements/loading'));
const Pagination = lazy(
	() => import('core/generic/components/navigation/pagination')
);

type NewsListProps = {
	firstName?: string;
	lastName?: string;
	name?: string;
	news: PlayerNews;
};

export const NewsList = ({
	firstName,
	lastName,
	name,
	news,
}: NewsListProps) => {
	const { viewportSize, isMobile, isDesktop } = useViewport();
	const pageSize = news.pageSize;
	const [page, setPage] = useState<number>(news.page);
	const [totalCount, setTotalCount] = useState<number>(news.totalCount);
	const [items, setItems] = useState(news.items);
	const [isLoading, setIsLoading] = useState(false);
	const { t, i18n } = useTranslation();
	const isFirstRenderRef = useRef(true);
	const contentBodyRef = useRef<HTMLDivElement>(null);
	const { scrollTopIfNeeded } = useScrollTo();
	const router = useBrowsing();
	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
	const { getValue: getPageParam, setValue: setPageParam } =
		useQueryParam('page');

	useEffect(() => {
		if (typeof window === 'undefined') return;
		if (!('scrollRestoration' in window.history)) return;

		const previous = window.history.scrollRestoration;
		window.history.scrollRestoration = 'manual';

		return () => {
			window.history.scrollRestoration = previous;
		};
	}, []);

	useEffect(() => {
		if (isFirstRenderRef.current) {
			isFirstRenderRef.current = false;
			return;
		}

		if (!router.isReady) return;

		const pageParam = getPageParam();
		const parsedPage = pageParam ? Number(pageParam) : null;
		const desiredPage =
			parsedPage && Number.isInteger(parsedPage) && parsedPage > 0
				? Math.min(parsedPage, totalPages)
				: 1;

		if (page !== desiredPage) {
			return;
		}

		fetchData();
		scrollTopIfNeeded({
			bodyRef: contentBodyRef,
		});
	}, [page, router.isReady, router.asPath, totalPages, getPageParam]);

	useEffect(() => {
		if (!router.isReady) return;

		const pageParam = getPageParam();
		const parsedPage = pageParam ? Number(pageParam) : null;

		if (!parsedPage || !Number.isInteger(parsedPage) || parsedPage <= 0) {
			if (page !== 1) {
				setPage(1);
			}
			return;
		}

		const nextPage = parsedPage > totalPages ? 1 : parsedPage;
		if (nextPage !== page) {
			setPage(nextPage);
		}

		if (parsedPage > totalPages) {
			setPageParam(String(nextPage));
		}
	}, [
		router.isReady,
		router.asPath,
		page,
		totalPages,
		getPageParam,
		setPageParam,
	]);

	const fetchData = async () => {
		setIsLoading(true);
		try {
			const response = await getNews({
				page,
				pageSize,
				tag: name,
				lang: i18n.language,
			});
			setItems(response.data.items);
			setTotalCount(response.data.totalCount);
			setIsLoading(false);
		} catch (error) {
			console.error('Error fetching news:', error);
			setIsLoading(false);
		}
	};

	const handlePageChange = (nextPage: number) => {
		setPageParam(String(nextPage));
	};

	return (
		<>
			<div
				className={`container ${styles['news-list']}`}
				ref={contentBodyRef}
			>
				{isLoading && (
					<div className={styles['loading']}>
						<Loading dimension="large" />
					</div>
				)}
				<h2 className={`display-large bold ${styles['title']}`}>
					{t('players_profile.heading_all_news', `All news`)}:{' '}
					{firstName} {lastName}
				</h2>
				<div
					className={`row ${isLoading ? styles['loading-list'] : ''}`}
				>
					{items.map((item, index) => (
						<div
							className="col-12 col-sm-4 col-lg-3"
							key={`${page}-${item.url ?? item.title ?? 'news'}-${
								item.schemaDate ?? index
							}`}
						>
							<NewsCard
								data={item}
								size={isDesktop ? 'Large' : 'Small'}
								direction={
									viewportSize === 'mobile' ? 'Row' : 'Column'
								}
							/>
							{index !== items.length - 1 && (
								<div className={styles['divider-wrapper']}>
									<Divider />
								</div>
							)}
						</div>
					))}
				</div>
			</div>
			{totalCount > pageSize && (
				<div className={`${styles['pagination-wrapper']} container`}>
					<Pagination
						currentPage={page}
						pageSize={pageSize}
						totalResults={totalCount}
						onPageChange={handlePageChange}
						colour="on-white"
						hideInfo={true}
						size={isMobile ? 'small' : 'medium'}
					/>
				</div>
			)}
		</>
	);
};
